import React, { useState, useEffect } from "react";
import { postData } from "../../utility/Utility";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactPaginate from "react-paginate";
import { CSVLink } from "react-csv";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";
import ViewInquiryModal from "../../Modal/ViewInquiryModal";
import WhatsAppModal from "../../Modal/WhatsAppModal";
import EmailModal from "../../Common/Emailmodale/Emailmodale";
import { Badge } from "react-bootstrap";
import AgentSelectionModal from "../../Modal/AgentSelectionModal";
import AssignmentHistoryModal from "../../Modal/AssignmentHistoryModal";
import LeadQuotationModal from "../../Modal/LeadQuotationModal";

export default function ContactFormInquiry() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [pageLimit, setPageLimit] = useState(10);
  const [cityFilterEnabled, setCityFilterEnabled] = useState(false);
  const [selectedInquiries, setSelectedInquiries] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [currentInquiryHistory, setCurrentInquiryHistory] = useState([]);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  // Toggle city filter
  const toggleCityFilter = (e) => {
    setCityFilterEnabled(e.target.checked);
    if (!e.target.checked) {
      setSearchTerm("");
    }
    setCurrentPage(0);
  };

  // Fetch inquiries from API
  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await postData("api/enquiries/list", {
        limit: pageLimit,
        page: currentPage + 1, // API uses 1-based index
        search: searchTerm.trim(), // Trim whitespace
        startDate: startDate,
        endDate: endDate,
        leadType: "contactpage",
      });

      if (response.success) {
        setInquiries(response?.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalInquiries(response.pagination?.totalItems || 0);
      } else {
        toast.error(response.message || "Failed to fetch inquiries");
      }
    } catch (error) {
      toast.error("Error fetching inquiries");
      console.error("Fetch inquiries error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchTerm,
    startDate,
    endDate,
    pageLimit,
    cityFilterEnabled,
  ]);

  // Handle page change
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchInquiries();
  };

  // Reset all filters
  const handleReset = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setCityFilterEnabled(false);
    setCurrentPage(0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Handle View sending
  const handleView = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowViewModal(true);
  };

  // Handle WhatsApp click
  const handleWhatsAppClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowWhatsAppModal(true);
  };

  // Handle Email click
  const handleEmailClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowEmailModal(true);
  };

  // Handle quotation upload
  const handleQuotationUpload = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowQuotationModal(true);
  };

  // Handle successful quotation upload
  const handleQuotationSuccess = (data) => {
    toast.success("Quotation uploaded successfully");
    setShowQuotationModal(false);
    fetchInquiries(); // Refresh the inquiries list
  };

  // Toggle selection for single inquiry
  const toggleInquirySelection = (inquiryId) => {
    setSelectedInquiries((prev) =>
      prev.includes(inquiryId)
        ? prev.filter((id) => id !== inquiryId)
        : [...prev, inquiryId]
    );
  };

  // Toggle select all
  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedInquiries(inquiries.map((inquiry) => inquiry._id));
    } else {
      setSelectedInquiries([]);
    }
  };

  const handleAgentSelect = async (agent) => {
    if (!agent?._id || selectedInquiries.length === 0) {
      toast.error("No agent or inquiries selected");
      return;
    }

    try {
      setLoading(true);
      const response = await postData("api/enquiries/assign", {
        enquiryIds: selectedInquiries,
        assignedTo: agent._id,
        assignedToModel: "User",
        note: `Assigned by admin at ${new Date().toLocaleString()}`,
      });

      if (response.success) {
        toast.success(
          `Successfully assigned ${selectedInquiries.length} inquiries to ${agent.name}`
        );
        fetchInquiries();
        setSelectedInquiries([]);
      } else {
        throw new Error(response.message || "Failed to assign inquiries");
      }
    } catch (error) {
      console.error("Assignment error:", error);
      toast.error(error.message || "Error assigning inquiries");
    } finally {
      setLoading(false);
      setShowAgentModal(false);
    }
  };

  const handleShowHistory = (inquiry) => {
    setCurrentInquiryHistory(inquiry.assignHistory || []);
    setShowHistoryModal(true);
  };

  // Prepare data for CSV export
  const prepareCSVData = () => {
    return inquiries.map((inquiry) => ({
      Name: inquiry.fullName,
      "Phone Number": inquiry.phoneNumber,
      Email: inquiry.email,
      "Enquiry Type": inquiry.enquiryType || "N/A",
      Date: formatDate(inquiry.createdAt),
      Message: inquiry.message || "N/A",
    }));
  };

  // CSV headers
  const csvHeaders = [
    { label: "Name", key: "Name" },
    { label: "Phone Number", key: "Phone Number" },
    { label: "Email", key: "Email" },
    { label: "Enquiry Type", key: "Enquiry Type" },
    { label: "Date", key: "Date" },
    { label: "Message", key: "Message" },
  ];

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />

      {/* View Inquiry Modal */}
      <ViewInquiryModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        inquiry={selectedInquiry}
      />

      {/* WhatsApp Modal */}
      <WhatsAppModal
        show={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        inquiry={selectedInquiry}
      />

      {/* Email Modal */}
      <EmailModal
        showEmailModal={showEmailModal}
        setShowEmailModal={setShowEmailModal}
        selectedInquiry={selectedInquiry}
      />

      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">
                Insurance Inquiries Management
              </h4>
              <p className="text-muted mb-0">
                Total Inquiries: {totalInquiries}
              </p>
            </div>
            <div className="mt-2 mt-sm-0">
              {inquiries?.length > 0 && (
                <>
                  <CSVLink
                    data={prepareCSVData()}
                    headers={csvHeaders}
                    filename={`insurance-inquiries-${new Date()
                      .toISOString()
                      .slice(0, 10)}.csv`}
                    className="btn btn-success me-2"
                  >
                    <i className="mdi mdi-file-excel me-1"></i> Export All
                  </CSVLink>
                  {selectedInquiries.length > 0 && (
                    <button
                      onClick={() => setShowAgentModal(true)}
                      className="btn btn-primary"
                    >
                      <i className="mdi mdi-file-export me-1"></i>
                      Assigned agent
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-end">
                      <div className="col-md-12 col-lg-2">
                        <label htmlFor="pageLimit" className="form-label">
                          Items per page
                        </label>
                        <select
                          className="form-select"
                          id="pageLimit"
                          value={pageLimit}
                          onChange={(e) => {
                            setPageLimit(Number(e.target.value));
                            setCurrentPage(0);
                          }}
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                      <div className="col-md-12 col-lg-2">
                        <div className="form-check form-switch d-flex align-items-center h-100">
                          <input
                            className="form-check-input me-2"
                            type="checkbox"
                            id="cityFilterToggle"
                            checked={cityFilterEnabled}
                            onChange={toggleCityFilter}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="cityFilterToggle"
                          >
                            City Filter
                          </label>
                        </div>
                      </div>
                      <div className="col-md-12 col-lg-3">
                        <label htmlFor="Search" className="form-label">
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder={
                              cityFilterEnabled
                                ? "Search disabled when city filter is active"
                                : "Search by name, email or mobile..."
                            }
                            value={searchTerm}
                            onChange={(e) =>
                              !cityFilterEnabled &&
                              setSearchTerm(e.target.value)
                            }
                            readOnly={cityFilterEnabled}
                          />
                          <span className="input-group-text">
                            <i
                              className={`mdi mdi-magnify ${cityFilterEnabled ? "text-muted" : ""
                                }`}
                            ></i>
                          </span>
                        </div>
                        {cityFilterEnabled && (
                          <small className="text-primary mt-1 d-block">
                            <i className="mdi mdi-information-outline me-1"></i>
                            Showing results for selected city only
                          </small>
                        )}
                      </div>

                      <div className="col-md-12 col-lg-2">
                        <label htmlFor="startDate" className="form-label">
                          From Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="startDate"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>

                      <div className="col-md-12 col-lg-2">
                        <label htmlFor="endDate" className="form-label">
                          To Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="endDate"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate}
                        />
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2 align-items-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Inquiries Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-centered mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}>
                              <input
                                type="checkbox"
                                checked={
                                  selectedInquiries.length ===
                                  inquiries.length && inquiries.length > 0
                                }
                                onChange={toggleSelectAll}
                              />
                            </th>
                            <th style={{ width: "80px" }}>S.No</th>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>Assigned count</th>
                            <th>Enquiry Type</th>
                            <th>Location</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inquiries?.length > 0 ? (
                            inquiries.map((inquiry, index) => (
                              <tr key={inquiry._id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedInquiries.includes(
                                      inquiry._id
                                    )}
                                    onChange={() =>
                                      toggleInquirySelection(inquiry._id)
                                    }
                                  />
                                </td>
                                <td className="text-center">
                                  {currentPage * pageLimit + index + 1}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-shrink-0 me-2">
                                      <div className="avatar-xs">
                                        <div className="avatar-title rounded-circle bg-light text-primary">
                                          {inquiry.fullName?.charAt(0) || "U"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h5 className="fs-14 mb-1">
                                        {inquiry.fullName}
                                      </h5>
                                    </div>
                                  </div>
                                </td>
                                <td>{inquiry.phoneNumber}</td>
                                <td>{inquiry.email}</td>
                                <td>
                                  {inquiry.assignHistory?.length > 0 ? (
                                    <Badge
                                      bg="primary"
                                      className="cursor-pointer"
                                      onClick={() => handleShowHistory(inquiry)}
                                    >
                                      {inquiry.assignHistory.length}{" "}
                                      {inquiry.assignHistory.length === 1
                                        ? "Assignment"
                                        : "Assignments"}
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary">Not Assigned</Badge>
                                  )}
                                </td>
                                <td>{inquiry.enquiryType || "N/A"}</td>

                                <td>
                                  {inquiry.location ? (
                                    <div className="d-flex align-items-center">
                                      <i className="mdi mdi-map-marker-outline me-1 text-danger"></i>
                                      <div className="d-flex flex-column">
                                        <span
                                          className="text-truncate"
                                          style={{ maxWidth: "150px" }}
                                          title={`${inquiry.location.city || ""
                                            } ${inquiry.location.state || ""}`}
                                        >
                                          {inquiry.location.city || ""}
                                          {inquiry.location.city &&
                                            inquiry.location.state
                                            ? ", "
                                            : ""}
                                          {inquiry.location.state || ""}
                                        </span>
                                        {inquiry.location.coordinates && (
                                          <a
                                            href={`https://www.google.com/maps?q=${inquiry.location.coordinates[1]},${inquiry.location.coordinates[0]}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary small d-flex align-items-center"
                                            style={{ fontSize: "0.75rem" }}
                                          >
                                            <i className="mdi mdi-map-marker-radius me-1"></i>
                                            View on Map
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-muted small">
                                      N/A
                                    </span>
                                  )}
                                </td>
                                <td>{formatDate(inquiry.createdAt)}</td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleView(inquiry)}
                                      title="View Details"
                                    >
                                      <i className="mdi mdi-eye"></i> View
                                    </button>
                                    <button
                                      className="btn btn-sm btn-warning"
                                      onClick={() => handleHistoryClick(inquiry)}
                                      title="View History"
                                    >
                                      <i className="mdi mdi-history"></i> History
                                    </button>
                                    <button
                                      className="btn btn-sm btn-info ms-1"
                                      onClick={() => handleQuotationUpload(inquiry)}
                                      title="Upload Quotation"
                                    >
                                      <i className="mdi mdi-file-pdf"></i> Quotation
                                    </button>
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() =>
                                        handleWhatsAppClick(inquiry)
                                      }
                                      title="Send WhatsApp"
                                    >
                                      <i className="mdi mdi-whatsapp"></i>{" "}
                                      WhatsApp
                                    </button>
                                    <button
                                      className="btn btn-sm btn-info"
                                      onClick={() => handleEmailClick(inquiry)}
                                      title="Send Email"
                                    >
                                      <i className="mdi mdi-email"></i> Email
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                No inquiries found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={
                            <i className="mdi mdi-chevron-left"></i>
                          }
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AgentSelectionModal
        show={showAgentModal}
        onClose={() => setShowAgentModal(false)}
        onSelect={handleAgentSelect}
      />

      <AssignmentHistoryModal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        history={currentInquiryHistory}
      />

      <LeadQuotationModal
        show={showQuotationModal}
        onClose={() => setShowQuotationModal(false)}
        leadId={selectedInquiry?._id}
        onSuccess={handleQuotationSuccess}
      />
    </div>
  );
}
